// // Localização: ./src/context/AuthContext.js (Versão 2.1.1 -)

import React, { createContext, useContext, useState, useEffect } from 'react';
import { jwtDecode } from 'jwt-decode'; 
import { setAuthToken, login } from '../api/api';
import { getAssinatura } from '../api/assinaturaApi';

const AuthContext = createContext();

// Função auxiliar para decodificar e extrair o usuário do token
// Usada primariamente para re-hidratação de tokens no localStorage (após refresh da página).
// Nota: O token NÃO contém as permissões, apenas o payload básico.
const getUserFromToken = (token) => {
    if (!token) return null;
    try {
        const decoded = jwtDecode(token); 
        
        // Verifica a expiração
        if (decoded.exp * 1000 < Date.now()) {
            console.warn("Token expirado.");
            return null;
        }
        
        // Retorna APENAS o payload do token. O objeto 'permissions' virá vazio aqui, 
        // mas será preenchido no login inicial e nas requisições subsequentes (pelo middleware).
        return {
            id: decoded.id, 
            nome: decoded.nome, // Usar 'nome' se for o nome da chave no payload do token
            email: decoded.email,
            role: decoded.role, 
            organizacao_id: decoded.organizacao_id,
            permissions: {} // Inicializamos vazio no refresh para evitar erros. O PrivateRoute deve lidar com isso.
        };

    } catch (e) {
        console.error("Erro ao decodificar token:", e);
        return null;
    }
};

export const AuthProvider = ({ children }) => {
    // Inicializa o estado lendo o token do localStorage
    const initialToken = localStorage.getItem('token');
    const initialUser = getUserFromToken(initialToken);
    
    const [token, setToken] = useState(initialToken);
    const [user, setUser] = useState(initialUser);
    const [subscription, setSubscription] = useState(null); // Estado da assinatura
    // Estado de loading inicial para verificar token após refresh
    const [loading, setLoading] = useState(!!initialToken); // Se há token inicial, estamos "carregando" para validar
    const [isInitialized, setIsInitialized] = useState(false);
    // Estado para rastrear primeiro carregamento após login (para mostrar página interativa)
    const [isInitialLoadAfterLogin, setIsInitialLoadAfterLogin] = useState(false);
    
    // ----------------------------------------------------
    // FUNÇÃO PARA BUSCAR ASSINATURA (definida antes de ser usada)
    // ----------------------------------------------------
    const fetchSubscription = async () => {
        try {
            const response = await getAssinatura();
            if (response.success && response.data) {
                setSubscription(response.data);
            } else {
                // Usuário sem assinatura não é erro
                setSubscription(null);
            }
        } catch (error) {
            // Não quebrar a aplicação se falhar ao buscar assinatura
            setSubscription(null);
        }
    };
    
    // ----------------------------------------------------
    // EFEITO DE INICIALIZAÇÃO (executa apenas uma vez na montagem)
    // ----------------------------------------------------
    useEffect(() => {
        const initializeAuth = async () => {
            if (initialToken) {
                // Sincroniza o token no Axios imediatamente
                setAuthToken(initialToken);
                
                // Valida o token ao inicializar
                const userFromToken = getUserFromToken(initialToken);
                if (userFromToken) {
                    // ✅ OTIMIZAÇÃO: Verificar cache de permissões antes de fazer requisição
                    const cachedPermissions = localStorage.getItem('user_permissions');
                    const cachedPermissionsTimestamp = localStorage.getItem('user_permissions_timestamp');
                    const cachedUserId = localStorage.getItem('user_permissions_user_id');
                    
                    // Cache válido por 30 minutos e apenas se for do mesmo usuário
                    const CACHE_DURATION = 30 * 60 * 1000; // 30 minutos (otimizado para melhor performance)
                    const now = Date.now();
                    const isCacheValid = cachedPermissions && 
                                       cachedPermissionsTimestamp && 
                                       cachedUserId === String(userFromToken.id) &&
                                       (now - parseInt(cachedPermissionsTimestamp)) < CACHE_DURATION;
                    
                    if (isCacheValid) {
                        try {
                            const permissions = JSON.parse(cachedPermissions);
                            setUser({ ...userFromToken, permissions });
                            console.log('[AuthContext] Permissões carregadas do cache');
                            
                            // ✅ OTIMIZAÇÃO: Não bloquear UI - permitir acesso imediato com cache
                            setIsInitialized(true);
                            setLoading(false);
                            
                            // Buscar assinatura e atualizar permissões em background (não bloqueia)
                            if (userFromToken.organizacao_id) {
                                fetchSubscription().catch(() => {}); // Não bloquear se falhar
                            }
                            
                            // ✅ OTIMIZAÇÃO: Atualizar permissões em background para garantir sincronização
                            // Mas não bloquear o acesso do usuário
                            (async () => {
                                try {
                                    const { getPermissions } = await import('../api/api');
                                    const response = await getPermissions();
                                    if (response.data && response.data.success && response.data.permissions) {
                                        const freshPermissions = response.data.permissions;
                                        setUser(prev => ({ ...prev, permissions: freshPermissions }));
                                        // Atualizar cache com permissões frescas
                                        localStorage.setItem('user_permissions', JSON.stringify(freshPermissions));
                                        localStorage.setItem('user_permissions_timestamp', String(Date.now()));
                                        localStorage.setItem('user_permissions_user_id', String(userFromToken.id));
                                        console.log('[AuthContext] Permissões atualizadas em background');
                                    }
                                } catch (error) {
                                    // Erro silencioso - não quebrar a aplicação
                                    console.warn('[AuthContext] Erro ao atualizar permissões em background:', error);
                                }
                            })();
                            
                            return; // ✅ OTIMIZAÇÃO: Retornar cedo, evitando chamada ao backend bloqueante
                        } catch (e) {
                            console.warn('[AuthContext] Erro ao parsear cache de permissões, buscando do backend');
                            // Continuar com o fluxo normal
                        }
                    }
                    
                    setUser(userFromToken);
                    // ✅ OTIMIZAÇÃO: Usar endpoint otimizado de permissões para carregamento mais rápido
                    try {
                        const { getPermissions } = await import('../api/api');
                        const response = await getPermissions();
                        if (response.data && response.data.success) {
                            const { permissions, user: userInfo } = response.data;
                            
                            // Atualizar usuário com permissões carregadas
                            if (permissions && Object.keys(permissions).length > 0) {
                                setUser({
                                    ...userFromToken,
                                    ...userInfo, // Atualizar dados do usuário se necessário
                                    permissions: permissions
                                });
                                
                                // ✅ OTIMIZAÇÃO: Salvar permissões no cache imediatamente
                                localStorage.setItem('user_permissions', JSON.stringify(permissions));
                                localStorage.setItem('user_permissions_timestamp', String(Date.now()));
                                localStorage.setItem('user_permissions_user_id', String(userFromToken.id));
                                
                                console.log('[AuthContext] Permissões carregadas do backend (endpoint otimizado)');
                            } else {
                                // Se não retornou permissões, manter usuário sem permissões
                                console.warn('[AuthContext] Nenhuma permissão retornada do backend');
                            }
                            
                            // Buscar assinatura se usuário tem organização (em background, não bloqueia)
                            if (userInfo?.organizacao_id || userFromToken.organizacao_id) {
                                fetchSubscription().catch(() => {}); // Não bloquear se falhar
                            }
                        } else {
                            // Fallback: tentar /usuarios/me se o novo endpoint não funcionar
                            console.warn('[AuthContext] Endpoint de permissões não retornou dados esperados, tentando fallback...');
                            const { get } = await import('../api/api');
                            const fallbackResponse = await get('/usuarios/me');
                            if (fallbackResponse.data) {
                                const userData = fallbackResponse.data.usuario || fallbackResponse.data;
                                if (userData && (userData.id || userData.email)) {
                                    setUser(userData);
                                    if (userData.permissions) {
                                        localStorage.setItem('user_permissions', JSON.stringify(userData.permissions));
                                        localStorage.setItem('user_permissions_timestamp', String(Date.now()));
                                        localStorage.setItem('user_permissions_user_id', String(userData.id));
                                    }
                                    if (userData.organizacao_id) {
                                        await fetchSubscription();
                                    }
                                }
                            }
                        }
                    } catch (error) {
                        // ✅ CORREÇÃO: Se o token for inválido (401), limpar tudo
                        if (error.response?.status === 401) {
                            console.warn('Token inválido ou expirado. Fazendo logout...');
                            setToken(null);
                            setUser(null);
                            setSubscription(null);
                            setAuthToken(null);
                            localStorage.removeItem('token');
                            localStorage.removeItem('user_permissions');
                            localStorage.removeItem('user_permissions_timestamp');
                            localStorage.removeItem('user_permissions_user_id');
                        } else {
                            console.warn('Não foi possível carregar permissões na inicialização:', error);
                            // Para outros erros (rede, etc), mantém o usuário do token decodificado
                            // As permissões serão carregadas na próxima requisição que usar o middleware
                        }
                    }
                } else {
                    // Token inválido ou expirado
                    setToken(null);
                    setUser(null);
                    setSubscription(null);
                    setAuthToken(null);
                    localStorage.removeItem('token');
                    localStorage.removeItem('user_permissions');
                    localStorage.removeItem('user_permissions_timestamp');
                    localStorage.removeItem('user_permissions_user_id');
                }
            }
            setIsInitialized(true);
            setLoading(false);
        };

        initializeAuth();
    }, []); // Executa apenas na montagem inicial
    
    // ----------------------------------------------------
    // EFEITO DE SINCRONIZAÇÃO E CONFIGURAÇÃO DO AXIOS (quando token muda)
    // ----------------------------------------------------
    useEffect(() => {
        // ✅ CORREÇÃO: Sincroniza o token no localStorage e Axios
        // Isso garante que mudanças no estado do token sejam refletidas no storage
        setAuthToken(token);
        
        // Se o token mudou, atualizamos o usuário (importante para refresh)
        if (token) {
            const newUserFromToken = getUserFromToken(token);
            
            // Se o usuário já tem permissões válidas, preserve-as
            if (user && user.permissions && Object.keys(user.permissions).length > 0) {
                setUser(prevUser => ({
                    ...newUserFromToken,
                    permissions: prevUser.permissions // Preserva o objeto permissions existente
                }));
            } else {
                setUser(newUserFromToken);
            }
        } else {
            // Token foi removido
            setUser(null);
        }
    }, [token]); // Depende apenas do token

    // ----------------------------------------------------
    // EFEITO: Recarregar assinatura quando organizacao_id do usuário mudar
    // Isso garante que usuários vinculados tenham acesso ao plano por extensão
    // ----------------------------------------------------
    const isAuthenticated = !!user && !!token; // Definir antes de usar
    
    useEffect(() => {
        // ✅ CORREÇÃO: Evitar loops - só executar quando realmente necessário
        // Se o usuário tem organizacao_id e está autenticado, buscar assinatura
        if (user && user.organizacao_id && isAuthenticated) {
            // Verificar se já temos assinatura carregada para esta organização
            const needsFetch = !subscription || (subscription.organizacao_id !== user.organizacao_id);
            if (needsFetch) {
                // Usar setTimeout para evitar múltiplas chamadas simultâneas
                const timeoutId = setTimeout(() => {
                    fetchSubscription();
                }, 100);
                return () => {
                    clearTimeout(timeoutId);
                };
            }
        } else if (user && !user.organizacao_id) {
            // ✅ CORREÇÃO: Não limpar assinatura se for trial Premium (trial não precisa de organização)
            const isTrialPremium = subscription?.is_trial && subscription?.status === 'TRIAL';
            if (!isTrialPremium && subscription) {
                // Se usuário não tem organização e não é trial Premium, limpar assinatura
                setSubscription(null);
            }
            // Se é trial Premium, manter a assinatura mesmo sem organizacao_id
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [user?.organizacao_id, isAuthenticated]); // Recarrega quando organizacao_id muda
    
    // ----------------------------------------------------
    // FUNÇÃO DE LOGOUT
    // ----------------------------------------------------
    const handleLogout = () => {
        setToken(null);
        setUser(null);
        setSubscription(null); // Limpar assinatura ao fazer logout
        setAuthToken(null);
        localStorage.removeItem('token'); // Garantir a remoção no LocalStorage
        // ✅ OTIMIZAÇÃO: Limpar cache de permissões ao fazer logout
        localStorage.removeItem('user_permissions');
        localStorage.removeItem('user_permissions_timestamp');
        localStorage.removeItem('user_permissions_user_id');
    };
    
    // ----------------------------------------------------
    // LISTENER PARA EVENTO DE LOGOUT (quando 401 é recebido)
    // ----------------------------------------------------
    useEffect(() => {
        const handleAuthLogout = (event) => {
            if (event.detail?.reason === 'unauthorized') {
                handleLogout();
            }
        };
        
        window.addEventListener('auth:logout', handleAuthLogout);
        return () => window.removeEventListener('auth:logout', handleAuthLogout);
    }, []);

    // ----------------------------------------------------
    // VERIFICAÇÃO DE ASSINATURA PENDENTE (quando usuário volta do checkout)
    // NOTA: Integração com Mercado Pago removida - será reimplementada com Stripe
    // verificamos se há uma assinatura pendente no localStorage e fazemos polling
    // ----------------------------------------------------
    useEffect(() => {
        // Só verificar se usuário estiver autenticado
        if (!isAuthenticated || !user) return;

        const pendingSubscriptionId = localStorage.getItem('pending_subscription_id');
        const pendingTimestamp = localStorage.getItem('pending_subscription_timestamp');

        if (!pendingSubscriptionId || !pendingTimestamp) return;

        // Verificar se passou menos de 30 minutos (para não fazer polling indefinidamente)
        const timestamp = parseInt(pendingTimestamp, 10);
        const now = Date.now();
        const timeElapsed = now - timestamp;
        const maxWaitTime = 30 * 60 * 1000; // 30 minutos

        if (timeElapsed > maxWaitTime) {
            localStorage.removeItem('pending_subscription_id');
            localStorage.removeItem('pending_subscription_timestamp');
            return;
        }

        // Função para verificar status da assinatura
        const checkPendingSubscription = async () => {
            try {
                // Buscar assinatura atualizada diretamente da API
                const { getAssinatura } = await import('../api/assinaturaApi');
                const response = await getAssinatura();

                if (response.success && response.data) {
                    const currentSubscription = response.data;
                    const subscriptionId = currentSubscription.id?.toString();
                    
                    if (subscriptionId === pendingSubscriptionId) {
                        // Atualizar estado local
                        setSubscription(currentSubscription);

                        if (currentSubscription.status === 'ATIVO') {
                            localStorage.removeItem('pending_subscription_id');
                            localStorage.removeItem('pending_subscription_timestamp');
                            
                            // Usar window.location para garantir navegação completa
                            window.location.href = '/config/assinatura/sucesso';
                            return;
                        } else if (currentSubscription.status === 'CANCELADO') {
                            localStorage.removeItem('pending_subscription_id');
                            localStorage.removeItem('pending_subscription_timestamp');
                            
                            window.location.href = '/config/assinatura/falha';
                            return;
                        }
                        // Se ainda está PENDENTE, continuar polling
                    }
                }
            } catch (error) {
                // Erro silencioso - não quebrar a aplicação
            }
        };

        // Verificar imediatamente
        checkPendingSubscription();

        // Fazer polling a cada 5 segundos enquanto assinatura estiver pendente
        const pollInterval = setInterval(() => {
            checkPendingSubscription();
        }, 5000); // 5 segundos

        // Limpar intervalo quando componente desmontar ou quando assinatura mudar para ATIVO/CANCELADO
        return () => {
            clearInterval(pollInterval);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isAuthenticated, user]); // Reexecutar quando autenticação mudar, mas não quando subscription mudar (evita loop)


    // ----------------------------------------------------
    // FUNÇÃO DE LOGIN
    // ----------------------------------------------------
    const handleLogin = async (email, senha) => {
        setLoading(true);
        setIsInitialLoadAfterLogin(true); // Marcar como primeiro carregamento após login
        
        try {
            // Usar a função 'login' do api.js, que já usa a baseURL correta do Axios.
            const response = await login({ email, password: senha });

            const data = response.data;

            // ✅ CORREÇÃO CRÍTICA: Salvar o token no localStorage IMEDIATAMENTE após o login
            // Isso garante que o token seja persistido antes de qualquer refresh (F5)
            if (data.token) {
                setAuthToken(data.token); // Salva no localStorage E configura o Axios
                setToken(data.token); // Atualiza o estado do contexto
            } else {
                throw new Error('Token não recebido do servidor.');
            }
            
            // Usar o objeto 'usuario' completo da resposta, que contém o campo 'permissions'
            if (data.usuario && data.usuario.permissions) {
                setUser(data.usuario);
                
                // ✅ OTIMIZAÇÃO: Salvar permissões no cache após login
                localStorage.setItem('user_permissions', JSON.stringify(data.usuario.permissions));
                localStorage.setItem('user_permissions_timestamp', String(Date.now()));
                localStorage.setItem('user_permissions_user_id', String(data.usuario.id));
                
                // Buscar assinatura após login se usuário tem organização
                if (data.usuario.organizacao_id) {
                    await fetchSubscription();
                }
            } else {
                console.error("Login successful but 'permissions' object is missing in the response user data.");
                // Fallback: usar o token e deixar o middleware da próxima requisição resolver
                const userFromToken = getUserFromToken(data.token);
                setUser(userFromToken);
                
                // Tentar buscar assinatura mesmo no fallback
                if (userFromToken?.organizacao_id) {
                    await fetchSubscription();
                }
            }
            
        } catch (error) {
            // Se houver erro, não é mais primeiro carregamento
            setIsInitialLoadAfterLogin(false);
            // Tratar erro do Axios aqui
            if (error.response) {
                // Erros 4xx, 5xx do servidor
                throw new Error(error.response.data.message || 'Erro de autenticação no servidor.');
            } else if (error.request) {
                // Erro de conexão (sem resposta do backend)
                throw new Error("Erro de conexão com o servidor. Verifique se o backend está ativo.");
            }
            throw error;
        } finally {
            setLoading(false);
        }
    };


    // ----------------------------------------------------
    // FUNÇÕES AUXILIARES DE ASSINATURA
    // ----------------------------------------------------
    const hasActiveSubscription = () => {
        if (!subscription) return false;
        
        // ✅ CORREÇÃO: Verificar se é trial (pode ter status 'ATIVO' ou 'TRIAL' no retorno)
        // O backend retorna is_trial: true e status: 'TRIAL' para trials, mesmo que no banco seja 'ATIVO'
        if (subscription.is_trial) {
            // Verificar se ainda está dentro do período de trial (14 dias)
            if (subscription.data_expiracao) {
                const hoje = new Date();
                const expiracao = new Date(subscription.data_expiracao);
                return expiracao >= hoje;
            }
            // Se não tem data_expiracao mas é trial, considerar ativo (deve ter sido criado recentemente)
            return true;
        }
        
        // Para assinaturas normais, verificar status ATIVO ou PENDENTE (aguardando confirmação de pagamento)
        // PENDENTE significa que o pagamento foi processado mas o webhook ainda não atualizou
        if (subscription.status === 'PENDENTE') {
            // Se tem stripe_subscription_id e não é trial, considerar como ativa temporariamente
            // O webhook vai atualizar para ATIVO em breve
            if (subscription.stripe_subscription_id && !subscription.stripe_subscription_id.startsWith('TRIAL_')) {
                return true; // Assinatura pendente de confirmação, mas considerada ativa
            }
        }
        
        // Para assinaturas ATIVAS, verificar status e data de expiração
        return subscription.status === 'ATIVO' && 
               (!subscription.data_expiracao || new Date(subscription.data_expiracao) >= new Date());
    };

    const getSubscriptionPlan = () => {
        if (!subscription) return null;
        return {
            id: subscription.plano_id,
            nome: subscription.plano_nome,
            valor: subscription.plano_valor,
            frequencia: subscription.plano_frequencia
        };
    };

    // ----------------------------------------------------
    // FUNÇÃO PARA ATUALIZAR DADOS DO USUÁRIO
    // Útil quando usuário é vinculado a uma organização
    // ----------------------------------------------------
    const refreshUserData = async () => {
        if (!token || !isAuthenticated) {
            return;
        }

        try {
            const { get } = await import('../api/api');
            const response = await get('/usuarios/me');
            if (response.data) {
                const userData = response.data;
                if (userData && (userData.id || userData.email)) {
                    // ✅ OTIMIZAÇÃO: Usar permissões da resposta se disponíveis, senão preservar existentes
                    setUser(prevUser => {
                        const finalPermissions = userData.permissions || prevUser?.permissions || {};
                        
                        // ✅ OTIMIZAÇÃO: Atualizar cache de permissões
                        if (userData.permissions) {
                            localStorage.setItem('user_permissions', JSON.stringify(userData.permissions));
                            localStorage.setItem('user_permissions_timestamp', String(Date.now()));
                            localStorage.setItem('user_permissions_user_id', String(userData.id));
                        }
                        
                        return {
                            ...userData,
                            permissions: finalPermissions
                        };
                    });
                    
                    // Se organizacao_id mudou, buscar assinatura
                    if (userData.organizacao_id) {
                        await fetchSubscription();
                    } else {
                        setSubscription(null);
                    }
                }
            }
        } catch (error) {
            console.warn('[AuthContext] Erro ao atualizar dados do usuário:', error);
            if (error.response?.status === 401) {
                handleLogout();
            }
        }
    };

    // ----------------------------------------------------
    // EFEITO: Desativar isInitialLoadAfterLogin quando carregamento terminar
    // ----------------------------------------------------
    useEffect(() => {
        if (isInitialLoadAfterLogin && !loading && isAuthenticated && user && isInitialized) {
            // Aguardar um pouco para garantir que tudo foi carregado
            // Isso permite que o usuário veja a página interativa por tempo suficiente
            const timer = setTimeout(() => {
                setIsInitialLoadAfterLogin(false);
            }, 2000); // 2 segundos após o carregamento terminar para dar tempo de ver o conteúdo
            return () => clearTimeout(timer);
        }
    }, [isInitialLoadAfterLogin, loading, isAuthenticated, user, isInitialized]);

    const value = {
        isAuthenticated, // Usar a variável já definida acima
        user,
        setUser, // Expor setUser para permitir atualizações do contexto
        token,
        setToken, // ✅ CORREÇÃO: Expor setToken para permitir atualizações do token
        loading,
        subscription, // Estado da assinatura
        isInitialLoadAfterLogin, // Estado para primeiro carregamento após login
        handleLogin,
        handleLogout,
        fetchSubscription, // Função para buscar/atualizar assinatura
        refreshUserData, // NOVO: Função para atualizar dados do usuário (útil após vinculação)
        hasActiveSubscription, // Verifica se tem assinatura ativa
        getSubscriptionPlan, // Retorna dados do plano atual
        // Adicionando um helper para checagem de permissão direta para o frontend
        hasPermission: (resource, action = 'can_access') =>
            user?.permissions?.[resource]?.[action] === 1,
    };

    return (
        <AuthContext.Provider value={value}>
            {children}
        </AuthContext.Provider>
    );
};

// Hook customizado para uso
export const useAuth = () => {
    return useContext(AuthContext);
};